import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ChevronLeft, Building, Users, Wallet, PiggyBank, FileBarChart,
    Plus, Trash2, Download, RefreshCw,
} from "lucide-react";

import { api, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
    { key: "cam",       label: "CAM Income",   icon: Users,       financial: true },
    { key: "salary",    label: "Salary",       icon: Users,       financial: false },
    { key: "upkeep",    label: "Upkeep",       icon: Wallet,      financial: false },
    { key: "petty",     label: "Petty Cash",   icon: PiggyBank,   financial: false },
    { key: "statement", label: "Statement",    icon: FileBarChart, financial: true },
];

const SECTION_LABEL = { ADMIN: "Admin", RECEPTIONIST: "Receptionist", SECURITY: "Security guards", HOUSEKEEPER: "Housekeepers" };
const FY_LABEL = (fy) => `FY ${fy}-${((fy + 1) % 100).toString().padStart(2, "0")}`;

const fmtMonth = (m) => new Date(`${m}-01T00:00:00`).toLocaleString("en-IN", { month: "short", year: "2-digit" });
const fmt = (n) => (n === undefined || n === null || n === 0) ? "" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function BuildingOpsPage() {
    const { siteId } = useParams();
    const [params, setParams] = useSearchParams();
    const { user } = useAuth();
    const hasFinancial = user?.effective_role === "OWNER" || user?.effective_role === "HEAD";
    const visibleTabs = TABS.filter((t) => !t.financial || hasFinancial);
    const wantedTab = params.get("tab") || (visibleTabs[0]?.key || "salary");
    const tab = visibleTabs.some((t) => t.key === wantedTab) ? wantedTab : visibleTabs[0]?.key;
    const [site, setSite] = useState(null);
    const [fyStart, setFyStart] = useState(() => {
        const now = new Date();
        return now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    });

    useEffect(() => {
        api.get(`/sites/${siteId}`).then(({ data }) => setSite(data)).catch((e) => toast.error(apiError(e)));
    }, [siteId]);

    if (!site) return <div className="mx-auto max-w-6xl p-6"><div className="h-64 rounded-xl bg-white animate-pulse" /></div>;

    const setTab = (t) => setParams({ tab: t }, { replace: true });

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3355] mb-3">
                <ChevronLeft className="w-4 h-4" /> Sites
            </Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Building operations</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2">
                        <Building className="w-6 h-6" /> {site.name}
                    </h1>
                    <div className="text-xs text-gray-500 mt-1">{site.address}</div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-500">Financial year</label>
                    <select
                        value={fyStart}
                        onChange={(e) => setFyStart(parseInt(e.target.value))}
                        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white"
                        data-testid="fy-selector"
                    >
                        {[fyStart + 1, fyStart, fyStart - 1, fyStart - 2].map((y) => (
                            <option key={y} value={y}>{FY_LABEL(y)}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="mt-6 flex gap-1 border-b border-gray-200 overflow-x-auto">
                {visibleTabs.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${active ? "border-[#1E3355] text-[#1E3355]" : "border-transparent text-gray-500 hover:text-[#1E3355]"}`}
                            data-testid={`tab-${t.key}`}
                        >
                            <Icon className="w-4 h-4" /> {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="mt-6">
                {tab === "cam" &&       <CamTab siteId={siteId} fyStart={fyStart} />}
                {tab === "salary" &&    <SalaryTab siteId={siteId} fyStart={fyStart} />}
                {tab === "upkeep" &&    <UpkeepTab siteId={siteId} fyStart={fyStart} />}
                {tab === "petty" &&     <PettyTab siteId={siteId} />}
                {tab === "statement" && <StatementTab siteId={siteId} />}
            </div>
        </div>
    );
}

// ---------- CAM TAB ----------
function CamTab({ siteId, fyStart }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ name: "", unit: "", monthly_amount: "" });

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get(`/buildings/${siteId}/cam?fy_start=${fyStart}`); setData(data); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteId, fyStart]);
    useEffect(() => { load(); }, [load]);

    async function addTenant(e) {
        e.preventDefault();
        try {
            await api.post(`/buildings/${siteId}/cam/tenants`, {
                name: form.name.trim(),
                unit: form.unit.trim() || null,
                monthly_amount: parseFloat(form.monthly_amount || "0"),
            });
            setShowAdd(false); setForm({ name: "", unit: "", monthly_amount: "" });
            toast.success("Tenant added"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function deleteTenant(tid) {
        if (!window.confirm("Delete this tenant and their overrides?")) return;
        try { await api.delete(`/buildings/${siteId}/cam/tenants/${tid}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(apiError(e)); }
    }
    async function saveCell(tid, month, value) {
        try {
            await api.patch(`/buildings/${siteId}/cam/cells/${tid}/${month}`, { amount: parseFloat(value || "0") });
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    if (loading || !data) return <SkeletonGrid />;
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-600">{data.rows.length} tenants · Grand total <span className="font-semibold text-[#1E3355]">₹{fmt(data.grand_total)}</span></div>
                {user?.role === "HO_ADMIN" && (
                    <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-3 py-1.5 rounded-md" data-testid="add-tenant-btn">
                        <Plus className="w-4 h-4" /> Add tenant
                    </button>
                )}
            </div>
            {showAdd && (
                <form onSubmit={addTenant} className="mb-4 rounded-md bg-white border border-gray-200 p-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <input placeholder="Tenant name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={FIELD} data-testid="new-tenant-name" />
                    <input placeholder="Unit / floor" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={FIELD} />
                    <input type="number" placeholder="Monthly amount" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })} className={FIELD} />
                    <div className="flex gap-2">
                        <button type="submit" className="flex-1 bg-[#1E3355] text-white text-sm font-semibold px-3 py-2 rounded-md" data-testid="save-tenant-btn">Add</button>
                        <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
                    </div>
                </form>
            )}
            <div className="overflow-x-auto rounded-xl bg-white border border-gray-100 fx-shadow-card">
                <table className="min-w-full text-sm">
                    <thead className="bg-[#F4F6F9] sticky top-0">
                        <tr>
                            <th className="text-left p-2 sticky left-0 bg-[#F4F6F9] z-10 min-w-[180px]">Tenant</th>
                            {data.months.map((m) => <th key={m} className="text-right p-2 min-w-[90px]">{fmtMonth(m)}</th>)}
                            <th className="text-right p-2 font-bold">Total</th>
                            {user?.role === "HO_ADMIN" && <th className="w-8" />}
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map((r) => (
                            <tr key={r.tenant.id} className="border-t border-gray-100 hover:bg-[#FAFBFC]">
                                <td className="p-2 sticky left-0 bg-white z-10 font-medium text-[#1E3355]" data-testid={`tenant-name-${r.tenant.name}`}>
                                    {r.tenant.name}
                                    {r.tenant.unit && <div className="text-[10px] text-gray-500">{r.tenant.unit}</div>}
                                </td>
                                {data.months.map((m) => (
                                    <td key={m} className="p-1">
                                        <CamCellInput value={r.cells[m].amount} isOverride={r.cells[m].is_override} onSave={(v) => saveCell(r.tenant.id, m, v)} testid={`cam-cell-${r.tenant.name}-${m}`} />
                                    </td>
                                ))}
                                <td className="p-2 text-right font-semibold text-[#1E3355]">₹{fmt(r.row_total)}</td>
                                {user?.role === "HO_ADMIN" && (
                                    <td className="p-1">
                                        <button onClick={() => deleteTenant(r.tenant.id)} className="text-[#E02B2B] hover:text-red-700 p-1" data-testid={`delete-tenant-${r.tenant.name}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        <tr className="border-t-2 border-gray-300 bg-[#F4F6F9] font-bold text-[#1E3355]">
                            <td className="p-2 sticky left-0 bg-[#F4F6F9]">Monthly TOTAL (A)</td>
                            {data.months.map((m) => <td key={m} className="p-2 text-right">₹{fmt(data.monthly_totals[m])}</td>)}
                            <td className="p-2 text-right">₹{fmt(data.grand_total)}</td>
                            {user?.role === "HO_ADMIN" && <td />}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CamCellInput({ value, isOverride, onSave, testid }) {
    const [v, setV] = useState(String(value ?? 0));
    useEffect(() => setV(String(value ?? 0)), [value]);
    return (
        <input
            type="number"
            value={v}
            onChange={(e) => setV(e.target.value)}
            onBlur={() => {
                if (parseFloat(v || "0") !== parseFloat(String(value ?? 0))) onSave(v);
            }}
            className={`w-full text-right px-2 py-1 text-xs rounded ${isOverride ? "bg-amber-50 border border-amber-200" : "bg-transparent hover:bg-gray-50 border border-transparent focus:border-[#1E3355]"} focus:outline-none`}
            data-testid={testid}
        />
    );
}

// ---------- SALARY TAB ----------
function SalaryTab({ siteId, fyStart }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ section: "HOUSEKEEPER", name: "", agency: "", notes: "" });

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/buildings/${siteId}/salary?fy_start=${fyStart}`); setData(data); }
        catch (e) { toast.error(apiError(e)); }
    }, [siteId, fyStart]);
    useEffect(() => { load(); }, [load]);

    async function addRow(e) {
        e.preventDefault();
        try {
            await api.post(`/buildings/${siteId}/salary/rows`, {
                section: form.section, name: form.name.trim(), agency: form.agency.trim() || null, notes: form.notes.trim() || null,
            });
            setForm({ section: "HOUSEKEEPER", name: "", agency: "", notes: "" });
            setShowAdd(false); toast.success("Row added"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function deleteRow(rid) {
        if (!window.confirm("Delete this row + all its monthly cells?")) return;
        try { await api.delete(`/buildings/${siteId}/salary/rows/${rid}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(apiError(e)); }
    }
    async function saveCell(rid, month, value) {
        try {
            await api.patch(`/buildings/${siteId}/salary/cells/${rid}/${month}`, { amount: parseFloat(value || "0") });
            load();
        } catch (e) { toast.error(apiError(e)); }
    }
    if (!data) return <SkeletonGrid />;
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-600">{data.amounts_masked ? "Amounts masked — Owner/Head only." : `Grand total ₹${fmt(data.grand_total)} — mirrors into Module 15 "Staff Salary — Agency" each month.`}</div>
                {user?.role === "HO_ADMIN" && (
                    <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-3 py-1.5 rounded-md" data-testid="add-salary-row-btn">
                        <Plus className="w-4 h-4" /> Add row
                    </button>
                )}
            </div>
            {showAdd && (
                <form onSubmit={addRow} className="mb-4 rounded-md bg-white border border-gray-200 p-4 grid grid-cols-1 sm:grid-cols-5 gap-3">
                    <select value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className={FIELD}>
                        {Object.entries(SECTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <input placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={FIELD} />
                    <input placeholder="Agency" value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} className={FIELD} />
                    <input placeholder="Notes (SD, etc.)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={FIELD} />
                    <div className="flex gap-2">
                        <button type="submit" className="flex-1 bg-[#1E3355] text-white text-sm font-semibold px-3 py-2 rounded-md">Add</button>
                        <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
                    </div>
                </form>
            )}
            <div className="space-y-4">
                {data.sections.map((s) => (
                    <div key={s.section} className="rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-x-auto">
                        <div className="p-3 border-b border-gray-100 bg-[#F4F6F9] font-display font-bold text-sm text-[#1E3355]">
                            {SECTION_LABEL[s.section] || s.section} · {s.rows.length} rows
                        </div>
                        <table className="min-w-full text-sm">
                            <thead className="bg-[#FAFBFC]">
                                <tr>
                                    <th className="text-left p-2 min-w-[160px]">Name / Agency</th>
                                    {data.months.map((m) => <th key={m} className="text-right p-2 min-w-[90px]">{fmtMonth(m)}</th>)}
                                    <th className="text-right p-2 font-bold">Total</th>
                                    {user?.role === "HO_ADMIN" && <th className="w-8" />}
                                </tr>
                            </thead>
                            <tbody>
                                {s.rows.map((r) => (
                                    <tr key={r.row.id} className="border-t border-gray-100">
                                        <td className="p-2">
                                            <div className="font-medium text-[#1E3355]" data-testid={`salary-name-${r.row.name}`}>{r.row.name}</div>
                                            <div className="text-[10px] text-gray-500">{r.row.agency || "direct"}{r.row.notes ? ` · ${r.row.notes}` : ""}</div>
                                        </td>
                                        {data.months.map((m) => (
                                            <td key={m} className="p-1">
                                                {r.cells[m].amount == null ? (
                                                    <div className="text-center text-xs text-gray-400 tracking-widest" data-testid={`salary-cell-masked-${r.row.name}-${m}`}>•••</div>
                                                ) : (
                                                    <CamCellInput value={r.cells[m].amount} isOverride={r.cells[m].amount > 0} onSave={(v) => saveCell(r.row.id, m, v)} testid={`salary-cell-${r.row.name}-${m}`} />
                                                )}
                                            </td>
                                        ))}
                                        <td className="p-2 text-right font-semibold text-[#1E3355]">{r.row_total == null ? "•••" : `₹${fmt(r.row_total)}`}</td>
                                        {user?.role === "HO_ADMIN" && (
                                            <td className="p-1">
                                                <button onClick={() => deleteRow(r.row.id)} className="text-[#E02B2B] hover:text-red-700 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                <tr className="border-t-2 border-gray-300 bg-[#F4F6F9] font-bold text-[#1E3355] text-xs">
                                    <td className="p-2">Section total</td>
                                    {data.months.map((m) => <td key={m} className="p-2 text-right">{s.monthly_totals[m] == null ? "•••" : `₹${fmt(s.monthly_totals[m])}`}</td>)}
                                    <td className="p-2 text-right">{data.amounts_masked ? "•••" : `₹${fmt(Object.values(s.monthly_totals).reduce((a, b) => a + (b || 0), 0))}`}</td>
                                    {user?.role === "HO_ADMIN" && <td />}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------- UPKEEP TAB ----------
function UpkeepTab({ siteId, fyStart }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/buildings/${siteId}/upkeep?fy_start=${fyStart}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
    }, [siteId, fyStart]);
    if (!data) return <SkeletonGrid />;
    // Filter to categories that have any value in the FY (keep signal:noise ratio high)
    const rows = data.categories.filter((r) => r.row_total > 0);
    return (
        <div>
            <div className="text-sm text-gray-600 mb-3">
                Building upkeep spend by category × month (Module 15 expenses filtered). Grand total <span className="font-semibold text-[#1E3355]">₹{fmt(data.grand_total)}</span>. Add expenses under <Link to={`/sites/${siteId}/expenses`} className="text-[#1E3355] underline">Expenses</Link>.
            </div>
            <div className="overflow-x-auto rounded-xl bg-white border border-gray-100 fx-shadow-card">
                <table className="min-w-full text-sm">
                    <thead className="bg-[#F4F6F9] sticky top-0">
                        <tr>
                            <th className="text-left p-2 min-w-[220px]">Category</th>
                            {data.months.map((m) => <th key={m} className="text-right p-2 min-w-[80px]">{fmtMonth(m)}</th>)}
                            <th className="text-right p-2 font-bold">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={data.months.length + 2} className="text-center p-6 text-gray-500 italic">No upkeep spend recorded yet in this FY.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.category.id} className="border-t border-gray-100 hover:bg-[#FAFBFC]">
                                <td className="p-2 font-medium text-[#1E3355]" data-testid={`upkeep-cat-${r.category.name}`}>{r.category.name}</td>
                                {data.months.map((m) => (
                                    <td key={m} className="p-2 text-right text-xs">{r.cells[m] > 0 ? `₹${fmt(r.cells[m])}` : "—"}</td>
                                ))}
                                <td className="p-2 text-right font-semibold text-[#1E3355]">₹{fmt(r.row_total)}</td>
                            </tr>
                        ))}
                        <tr className="border-t-2 border-gray-300 bg-[#F4F6F9] font-bold text-[#1E3355]">
                            <td className="p-2">Monthly TOTAL</td>
                            {data.months.map((m) => <td key={m} className="p-2 text-right">₹{fmt(data.monthly_totals[m])}</td>)}
                            <td className="p-2 text-right">₹{fmt(data.grand_total)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- PETTY TAB ----------
function PettyTab({ siteId }) {
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [data, setData] = useState(null);
    const [showAddFloat, setShowAddFloat] = useState(false);
    const [floatForm, setFloatForm] = useState({ date: new Date().toISOString().slice(0, 10), amount: "", note: "" });
    const [lineForm, setLineForm] = useState({ date: new Date().toISOString().slice(0, 10), description: "", amount: "" });

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/buildings/${siteId}/petty?month=${month}`); setData(data); }
        catch (e) { toast.error(apiError(e)); }
    }, [siteId, month]);
    useEffect(() => { load(); }, [load]);

    async function addFloat(e) {
        e.preventDefault();
        try {
            await api.post(`/buildings/${siteId}/petty/floats`, { date: floatForm.date, amount: parseFloat(floatForm.amount), note: floatForm.note });
            setFloatForm({ date: floatForm.date, amount: "", note: "" }); setShowAddFloat(false);
            toast.success("Float added"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function addLine(e) {
        e.preventDefault();
        try {
            await api.post(`/buildings/${siteId}/petty/lines`, { date: lineForm.date, description: lineForm.description, amount: parseFloat(lineForm.amount) });
            setLineForm({ ...lineForm, description: "", amount: "" });
            toast.success("Line added"); load();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function deleteLine(lid) {
        if (!window.confirm("Delete this line?")) return;
        try { await api.delete(`/buildings/${siteId}/petty/lines/${lid}`); load(); }
        catch (e) { toast.error(apiError(e)); }
    }

    if (!data) return <SkeletonGrid />;
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={FIELD + " w-40"} data-testid="petty-month" />
                <div className={`text-sm font-semibold px-3 py-1.5 rounded-md ${data.balance_zero ? "bg-emerald-50 text-emerald-700" : Math.abs(data.running_balance) > 0.01 ? "bg-amber-50 text-amber-700" : ""}`} data-testid="petty-balance">
                    Balance: ₹{fmt(data.running_balance)} {data.balance_zero ? " · Zero ✓" : ""}
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Floats */}
                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="font-display font-bold text-[#1E3355]">Floats received</h3>
                        <button onClick={() => setShowAddFloat((s) => !s)} className="text-xs text-[#1E3355] hover:underline" data-testid="add-float-toggle">+ Add float</button>
                    </div>
                    {showAddFloat && (
                        <form onSubmit={addFloat} className="mb-3 space-y-2">
                            <input type="date" required value={floatForm.date} onChange={(e) => setFloatForm({ ...floatForm, date: e.target.value })} className={FIELD} />
                            <input type="number" required placeholder="Amount ₹" value={floatForm.amount} onChange={(e) => setFloatForm({ ...floatForm, amount: e.target.value })} className={FIELD} data-testid="new-float-amount" />
                            <input placeholder="Note (optional)" value={floatForm.note} onChange={(e) => setFloatForm({ ...floatForm, note: e.target.value })} className={FIELD} />
                            <button type="submit" className="w-full bg-[#1E3355] text-white text-sm py-2 rounded-md" data-testid="save-float-btn">Add float</button>
                        </form>
                    )}
                    <div className="text-xs text-gray-500 mb-1">Total received: ₹{fmt(data.total_float)}</div>
                    <ul className="space-y-1 text-sm">
                        {data.floats.map((f) => (
                            <li key={f.id} className="flex justify-between border-b border-gray-100 py-1">
                                <span className="text-gray-500 text-xs">{fmtDate(f.date)}{f.note ? ` · ${f.note}` : ""}</span>
                                <span className="font-semibold">₹{fmt(f.amount)}</span>
                            </li>
                        ))}
                        {data.floats.length === 0 && <li className="text-xs text-gray-400 italic">No floats this month.</li>}
                    </ul>
                </div>
                {/* Lines + Add form */}
                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4 lg:col-span-2">
                    <h3 className="font-display font-bold text-[#1E3355] mb-2">Expense lines</h3>
                    <form onSubmit={addLine} className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-3">
                        <input type="date" required value={lineForm.date} onChange={(e) => setLineForm({ ...lineForm, date: e.target.value })} className={FIELD} />
                        <input required placeholder="Description" value={lineForm.description} onChange={(e) => setLineForm({ ...lineForm, description: e.target.value })} className={FIELD + " sm:col-span-2"} data-testid="new-line-desc" />
                        <input type="number" required placeholder="Amount ₹" value={lineForm.amount} onChange={(e) => setLineForm({ ...lineForm, amount: e.target.value })} className={FIELD} data-testid="new-line-amount" />
                        <button type="submit" className="bg-[#1E3355] text-white text-sm rounded-md" data-testid="save-line-btn">Add</button>
                    </form>
                    <div className="text-xs text-gray-500 mb-1">Total spent: ₹{fmt(data.total_lines)} · Mirrored to Module 15 as "Petty Cash Expenses".</div>
                    <table className="w-full text-sm">
                        <tbody>
                            {data.lines.map((l) => (
                                <tr key={l.id} className="border-b border-gray-100">
                                    <td className="py-1.5 text-xs text-gray-500 w-24">{fmtDate(l.date)}</td>
                                    <td className="py-1.5">{l.description}</td>
                                    <td className="py-1.5 text-right font-semibold w-24">₹{fmt(l.amount)}</td>
                                    <td className="w-8 text-right"><button onClick={() => deleteLine(l.id)} className="text-[#E02B2B] p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                                </tr>
                            ))}
                            {data.lines.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-xs text-gray-400 italic">No lines this month.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ---------- STATEMENT TAB ----------
function StatementTab({ siteId }) {
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get(`/buildings/${siteId}/statement?month=${month}`); setData(data); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteId, month]);
    useEffect(() => { load(); }, [load]);

    async function dl(kind) {
        try {
            const url = `/buildings/${siteId}/statement/export.${kind}?month=${month}`;
            const { data } = await api.get(url, { responseType: "blob" });
            const blob = new Blob([data], { type: kind === "pdf" ? "application/pdf" : "text/csv" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `statement-${month}.${kind}`;
            link.click();
        } catch (e) { toast.error(apiError(e)); }
    }

    if (loading || !data) return <SkeletonGrid />;
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={FIELD + " w-40"} data-testid="statement-month" />
                <div className="flex gap-2">
                    <button onClick={() => dl("csv")} className="inline-flex items-center gap-1 bg-white border border-gray-200 hover:border-[#1E3355] text-[#1E3355] text-sm px-3 py-1.5 rounded-md" data-testid="export-csv"><Download className="w-4 h-4" /> CSV</button>
                    <button onClick={() => dl("pdf")} className="inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm px-3 py-1.5 rounded-md" data-testid="export-pdf"><Download className="w-4 h-4" /> PDF</button>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <StatCard label="Total CAM Income (A)" value={data.income_total} tone="income" />
                <StatCard label="Total Expenses (B)" value={data.expense_total} tone="expense" />
                <StatCard label="NET (A − B)" value={data.net} tone={data.net >= 0 ? "positive" : "negative"} testid="statement-net" />
            </div>
            <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4">
                <h3 className="font-display font-bold text-[#1E3355] mb-2 text-sm uppercase tracking-wider">Expenses by category</h3>
                {data.by_category.length === 0 ? (
                    <div className="text-sm text-gray-400 italic">No expenses recorded this month.</div>
                ) : (
                    <table className="w-full text-sm">
                        <tbody>
                            {data.by_category.map((r) => (
                                <tr key={r.category} className="border-b border-gray-100">
                                    <td className="py-1.5">{r.category}</td>
                                    <td className="py-1.5 text-right font-semibold">₹{fmt(r.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function StatCard({ label, value, tone, testid }) {
    const cls = {
        income:   "bg-emerald-50 border-emerald-200 text-emerald-800",
        expense:  "bg-red-50 border-red-200 text-red-800",
        positive: "bg-[#EEF2F8] border-[#C7D1E2] text-[#1E3355]",
        negative: "bg-red-50 border-red-200 text-red-800",
    }[tone] || "bg-white border-gray-200 text-[#1E3355]";
    return (
        <div className={`rounded-xl border p-4 ${cls}`} data-testid={testid}>
            <div className="text-xs font-medium uppercase tracking-wider opacity-70">{label}</div>
            <div className="font-display text-2xl font-bold mt-1">₹{fmt(value)}</div>
        </div>
    );
}

function SkeletonGrid() { return <div className="h-64 rounded-xl bg-white animate-pulse" />; }

const FIELD = "rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355]";
